import Section from "@intility/bifrost-react/Section";
import { transformer } from "@openfga/syntax-transformer";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import type { AuthorizationModel } from "~/api/fga-types";
import { getManagementModel } from "~/api/management";
import { PageLayout } from "~/components/layout/PageLayout";
import ModelDetails from "~/components/model/ModelDetails";

export default function ManagementAuthorizationModelDetail() {
  const { modelId } = useParams<{ modelId: string }>();
  const [model, setModel] = useState<AuthorizationModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!modelId) return;
    getManagementModel(modelId)
      .then((res) => setModel(res))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [modelId]);

  const dsl = useMemo(() => {
    if (!model) return null;
    try {
      return transformer.transformJSONToDSL({
        schema_version: model.schemaVersion,
        type_definitions: model.typeDefinitions,
      } as Parameters<typeof transformer.transformJSONToDSL>[0]);
    } catch {
      return null;
    }
  }, [model]);

  if (loading) return <p className="bf-p">Loading model...</p>;
  if (error) return <p className="bf-p bfc-alert">Error: {error}</p>;
  if (!model) return <p className="bf-p">Model not found</p>;

  return (
    <PageLayout
      title="Management Authorization Model"
      level={2}
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Management" },
        { label: "Model", to: "/management/model" },
        { label: "Version" },
      ]}
      description={
        <>
          ID: <code>{model.id}</code> &middot; Schema: {model.schemaVersion}
        </>
      }
    >
      {dsl && (
        <Section>
          <Section.Header>DSL</Section.Header>
          <pre
            style={{
              margin: 0,
              padding: 16,
              fontFamily: "monospace",
              fontSize: 13,
              lineHeight: 1.5,
              whiteSpace: "pre",
              overflowX: "auto",
              backgroundColor: "var(--bf-base-2-bg)",
              color: "var(--bf-base-color)",
            }}
          >
            {dsl}
          </pre>
        </Section>
      )}

      <ModelDetails typeDefinitions={model.typeDefinitions ?? []} />
    </PageLayout>
  );
}
